import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Select,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { ModalTitle } from 'components';
import { useApiRequests } from 'hooks';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { FolderMemberPermissionDto, FolderAccessLevel, UpdateFolderPermissionsRequest } from 'types/document';

import styles from './ManageAccessModal.module.scss';

function normalizeAccessLevel(value: FolderAccessLevel | string | null): FolderAccessLevel | null {
  if (value === null) return null;

  if (typeof value === 'number') return value;

  return FolderAccessLevel[value as keyof typeof FolderAccessLevel] ?? null;
}

type Props = {
  folderId: string;
  folderName: string;
  onClose: () => void;
};

const ACCESS_LABELS: Record<FolderAccessLevel, string> = {
  [FolderAccessLevel.NoAccess]: 'NoAccess',
  [FolderAccessLevel.Read]: 'Read',
  [FolderAccessLevel.Write]: 'Write',
  [FolderAccessLevel.Full]: 'Full',
};

// simple heuristic: DB ids are GUID-like, names are not
const isValidFolderId = (id: string) => id?.length >= 32;

export default function ManageAccessModal({ folderId, folderName, onClose }: Props) {
  const { t } = useTranslation('settings');
  const { getFolderPermissions, updateFolderPermissions } = useApiRequests();

  const [members, setMembers] = useState<FolderMemberPermissionDto[]>([]);
  const [originalMembers, setOriginalMembers] = useState<FolderMemberPermissionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const canLoadPermissions = isValidFolderId(folderId);

  console.log('🔁 ManageAccessModal render', {
    folderId,
    members: members.map((m) => ({
      id: m.clinicUserId,
      access: m.accessLevel,
    })),
  });

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!canLoadPermissions) {
        setMembers([]);
        setOriginalMembers([]);
        setLoading(false);
        return;
      }

      try {
        const result = await getFolderPermissions(folderId);
        if (mounted) {
          const cloned = result
            .filter((m) => m.via3UserType === 'Member') // 🔑 ONLY MEMBERS
            .map((m) => ({
              ...m,
              accessLevel: normalizeAccessLevel(m.accessLevel) ?? FolderAccessLevel.NoAccess,
            }));

          setMembers(cloned);
          setOriginalMembers(cloned.map((m) => ({ ...m })));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [folderId, canLoadPermissions]); // 🔥 FIXED

  const hasChanges = useMemo(() => {
    if (members.length !== originalMembers.length) return true;

    const originalMap = new Map(originalMembers.map((m) => [m.clinicUserId, m.accessLevel]));

    return members.some((m) => originalMap.get(m.clinicUserId) !== m.accessLevel);
  }, [members, originalMembers]);

  const canManageAccess = true;

  const getUserTypeLabel = (via3UserType: string): string => {
    const key = `manageAccess.userTypes.${via3UserType}`;
    const translated = t(key);
    return translated === key ? via3UserType : translated;
  };

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const aHasAccess = a.accessLevel !== FolderAccessLevel.NoAccess;
      const bHasAccess = b.accessLevel !== FolderAccessLevel.NoAccess;

      if (aHasAccess !== bHasAccess) return aHasAccess ? -1 : 1;
      return a.userName.localeCompare(b.userName);
    });
  }, [members]);

  const filteredMembers = useMemo(() => {
    if (!searchTerm.trim()) return sortedMembers;

    const q = searchTerm.toLowerCase();

    return sortedMembers.filter((m) => {
      return m.userName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q);
    });
  }, [sortedMembers, searchTerm]);

  const updateAccess = (clinicUserId: string, accessLevel: FolderAccessLevel) => {
    console.log('🟡 DROPDOWN CHANGE FIRED', {
      clinicUserId,
      newAccess: accessLevel,
      type: typeof accessLevel,
    });

    setMembers((prev) => {
      const next = prev.map((m) => (m.clinicUserId === clinicUserId ? { ...m, accessLevel } : m));

      console.log(
        '🟢 MEMBERS STATE AFTER UPDATE',
        next.map((m) => ({
          id: m.clinicUserId,
          access: m.accessLevel,
        })),
      );

      return next;
    });
  };

  const handleSave = async () => {
    if (!canLoadPermissions) return;

    setSaving(true);

    const payload: UpdateFolderPermissionsRequest = {
      folderId,
      permissions: members.map((m) => ({
        clinicUserId: m.clinicUserId,
        userName: m.userName,
        email: m.email,
        via3UserType: m.via3UserType,
        isCurrentUser: m.isCurrentUser,

        // 🔑 CRITICAL FIX
        accessLevel: m.accessLevel ?? FolderAccessLevel.NoAccess,
      })),
    };

    try {
      await updateFolderPermissions(payload);

      enqueueSnackbar(t('manageAccess.folderPermissionsUpdated'), {
        variant: 'success',
      });

      onClose();
    } catch (err) {
      enqueueSnackbar(t('manageAccess.failedToUpdatePermissions'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 400,
          maxWidth: 400,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <ModalTitle title={t('manageAccess.manageAccessUpper')} onClose={onClose} />

        <Typography
          variant='body2'
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mt: 0.5,
          }}
        >
          {folderName}
        </Typography>
      </DialogTitle>

      <DialogContent className={styles.content}>
        {!canLoadPermissions ? (
          <Typography variant='body2' color='text.secondary'>
            {t('manageAccess.accessOnlyForFolders')}
          </Typography>
        ) : loading ? (
          <Typography variant='body2'>{t('manageAccess.loadingMembers')}</Typography>
        ) : (
          <>
            {/* SEARCH */}
            <Box sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                size='small'
                placeholder={t('manageAccess.searchByNameOrEmail')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  sx: {
                    fontSize: '0.9rem',
                  },
                }}
              />
            </Box>
            <Box className={styles.memberList}>
              {/* HEADER ROW */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 0, // ✅ no left/right padding
                  pb: 0.5,
                }}
              >
                <Typography variant='caption' sx={{ fontSize: '1.2rem', fontWeight: 600, color: 'text.primary' }}>
                  {t('manageAccess.name')}
                </Typography>

                <Typography variant='caption' sx={{ fontSize: '1.2rem', fontWeight: 600, color: 'text.primary' }}>
                  {t('manageAccess.permission')}
                </Typography>
              </Box>

              <Box
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  mb: 1,
                }}
              />

              {filteredMembers.length === 0 ? (
                <Box className={styles.emptyStateRow}>
                  <Typography variant='body2' color='text.secondary'>
                    {t('manageAccess.noMatchingMembers')}
                  </Typography>
                </Box>
              ) : (
                filteredMembers.map((member) => (
                  <Box
                    key={member.clinicUserId}
                    className={styles.memberRow}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      py: 0.75,
                    }}
                  >
                    {/* LEFT: Name + role/email */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0, // 🔑 required for ellipsis
                        flex: 1,
                      }}
                    >
                      <Typography
                        variant='body1'
                        sx={{
                          fontSize: '1.2rem',
                          lineHeight: 1.3,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        className={styles.memberName}
                      >
                        {member.userName}
                        {member.isCurrentUser && <span className={styles.youLabel}> ({t('manageAccess.you')})</span>}
                      </Typography>

                      <Typography
                        variant='body2'
                        sx={{
                          fontSize: '0.85rem',
                          color: 'text.secondary',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getUserTypeLabel(member.via3UserType)} ({member.email})
                      </Typography>
                    </Box>

                    {/* RIGHT: Permission dropdown */}
                    <Select
                      size='small'
                      value={member.accessLevel ?? FolderAccessLevel.NoAccess}
                      disabled={member.isCurrentUser}
                      onChange={(e) => {
                        console.log('🔵 SELECT onChange', {
                          rawValue: e.target.value,
                          type: typeof e.target.value,
                        });

                        updateAccess(member.clinicUserId, e.target.value as FolderAccessLevel);
                      }}
                      sx={{
                        fontSize: '0.8125rem',
                        height: 32,
                        flexShrink: 0, // 🔑 prevents being pushed
                        '& .MuiSelect-select': {
                          py: 0.5,
                        },
                      }}
                    >
                      {Object.values(FolderAccessLevel)
                        .filter((v) => typeof v === 'number')
                        .filter((level) => {
                          if (level === FolderAccessLevel.Full && member.via3UserType === 'Member') {
                            return false;
                          }
                          return true;
                        })
                        .map((level) => (
                          <MenuItem key={level} value={level}>
                            {t(`manageAccess.accessLevels.${ACCESS_LABELS[level as FolderAccessLevel]}`)}
                          </MenuItem>
                        ))}
                    </Select>
                  </Box>
                ))
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Stack direction='row' className={styles.actionButtons}>
          <Button variant='outlined' fullWidth onClick={onClose} disabled={saving}>
            {t('manageAccess.cancel')}
          </Button>

          {canLoadPermissions && canManageAccess && (
            <Button variant='contained' fullWidth onClick={handleSave} disabled={saving || !hasChanges}>
              {t('manageAccess.save')}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
